import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAppVideoScreensPaginatedQuery,
  useGetAppOnboardingVideoScreensQuery,
  useGetVideoStoreScreensQuery,
} from "@/features/auth/api/authAPI";
import { filterAppsDataActions } from "@/entities/filterAppsData";
import { getFilterState } from "@/entities/filter";

import styles from "./SavedLibraryFlowModal.module.scss";
import { useMe } from "@/Hooks";

import { RightClickModal } from "@/shared/ui/RightClickModal/RightClickModal";
import { handleOpenVideoPage } from "@/app/libs/const/handleOpenVideoPage";
import PhotoItemNew from "@/Views/Virtual/PhotoItemNew";
import { useOpenProModal } from "@/Hooks/useOpenBillingModal";
import Modal from "react-modal";
import { XIcon } from "lucide-react";
import Loader from "@/shared/ui/Loader/Loader";
import SelectedScreensPopup, {
  SelectedScreenType,
} from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import {
  useSelectedScreens,
  useSelectedAppStoreScreens,
  useSelectedOnboardingScreens,
} from "@/entities/selectedScreens";
import InfoPhoneHomePageHorizontal from "@/shared/ui/InfoPhoneHomePageHorizontal/InfoPhoneHomePageHorizontal";
import { formatNumber } from "@/widgets/phoneHomePageHorisontal/libs/const/formatNumber";

export interface IAppVideoScreensData {
  id: number;
  screen: string;
  timestamp: string;
  labels: string[];
  app_video?: number;
  name?: string;
}

export interface IImages extends IAppVideoScreensData {
  highlighter: boolean;
  isGrouped: boolean;
  label: string;
  is_saved?: boolean;
  is_saved_pk?: number;
  is_blurry?: boolean;
}

interface DisplayDataItem {
  label: string;
  images: IImages[];
  isGrouped: boolean;
}

interface Organization {
  uuid: string;
  is_pro: boolean;
}

interface User {
  email: string;
  username: string;
  is_pro: boolean;
}

interface SavedLibraryFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    image: string;
    name: string;
    ts: number;
    id: number;
    idVideo: number;
    type: string;
    imageStoreId: number;
    idStore?: number;
    appId?: number;
    slug?: string;
    appData?: any;
    viewType: string;
  };
  currentOrganization: Organization;
  onItemsAdded?: (newItems: any[]) => void;
  onItemsRemoved?: (removedItemIds: number[]) => void;
}

const MemoizedPhotoItemNew = React.memo(PhotoItemNew);

const SavedLibraryFlowModal: React.FC<SavedLibraryFlowModalProps> = ({
  isOpen,
  onClose,
  data,
  currentOrganization,
  onItemsAdded,
  onItemsRemoved,
}) => {
  const dispatch = useDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const phoneHomePageHorizontalRef = useRef<HTMLDivElement>(null);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);
  const imageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [, setHighlightedImages] = useState<string[]>([]);

  const { me } = useMe() as { me: User };
  const [appVideoScreens, setAppVideoScreens] = useState<IImages[]>([]);
  const [uniqueData, setUniqueData] = useState<DisplayDataItem[]>([]);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Add new state for tracking screen width
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 768);

  // Add selectedScreens hooks based on viewType
  const appReplayScreens = useSelectedScreens();
  const appStoreScreens = useSelectedAppStoreScreens();
  const onboardingScreens = useSelectedOnboardingScreens();

  // Smart hook selection based on viewType
  const selectedScreensHook = useMemo(() => {
    switch (data.viewType) {
      case "video":
        return appReplayScreens;
      case "store":
        return appStoreScreens;
      case "webonboarding":
        return onboardingScreens;
      default:
        return appReplayScreens;
    }
  }, [data.viewType, appReplayScreens, appStoreScreens, onboardingScreens]);

  // Get selected screens count for conditional rendering
  const selectedScreensCount = selectedScreensHook.selectedScreens.length;

  // Track whether initial scroll has been performed
  const hasInitialScrolled = useRef(false);

  // Add effect to update screen width state
  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset scroll flag when modal opens
  useEffect(() => {
    if (isOpen) {
      hasInitialScrolled.current = false;
    }
  }, [isOpen]);

  // Fetch data based on viewType with correct parameters
  const {
    data: videoScreensData,
    isLoading: isVideoLoading,
    isFetching: isVideoFetching,
    error: videoError,
    refetch: refetchVideoScreens,
  } = useGetAppVideoScreensPaginatedQuery(
    {
      appId: data.appId,
      app_video: data.idVideo,
      pageSize: 1000,
      page: 1,
      featured_only: false,
    },
    {
      skip: data.viewType !== "video" || !data.idVideo,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  const {
    data: onboardingScreensData,
    isLoading: isOnboardingLoading,
    isFetching: isOnboardingFetching,
    error: onboardingError,
    refetch: refetchOnboardingScreens,
  } = useGetAppOnboardingVideoScreensQuery(
    {
      app_ob_video: data.idVideo,
      page_size: 1000,
      page: 1,
    },
    {
      skip: data.viewType !== "webonboarding" || !data.idVideo,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: storeScreensData,
    isLoading: isStoreLoading,
    isFetching: isStoreFetching,
    error: storeError,
    refetch: refetchStoreScreens,
  } = useGetVideoStoreScreensQuery(
    {
      appId: data.appId || 0,
    },
    {
      skip: data.viewType !== "store" || !data.appId,
      refetchOnMountOrArgChange: true,
    },
  );

  const organization = currentOrganization as Organization;

  // Get app info based on viewType and data structure
  const getAppInfo = () => {
    if (data.viewType === "webonboarding" && data.appData) {
      // For webonboarding, data comes from dataSavedPoint.results which has app.name, app.icon, app.shortname
      return {
        name: data.appData.app?.name || data.name,
        icon: data.appData.app?.icon || "",
        shortname: data.appData.app?.shortname || "",
      };
    } else if (data.viewType === "store" && data.appData) {
      // For store, data comes from apiDataSavedStoreScreens.results which has app.name, app.icon, app.shortname
      return {
        name: data.appData.app?.name || data.name,
        icon: data.appData.app?.icon || "",
        shortname: data.appData.app?.shortname || "",
      };
    } else if (data.viewType === "video" && data.appData) {
      // For video/replay, data comes from dataSavedPoint.results which has app.name, app.icon, app.shortname
      return {
        name: data.appData.app?.name || data.name,
        icon: data.appData.app?.icon || "",
        shortname: data.appData.app?.shortname || "",
      };
    }

    // Fallback
    return {
      name: data.name,
      icon: "",
      shortname: "",
    };
  };

  const appInfo = getAppInfo();

  // Process data based on viewType
  useEffect(() => {
    let processedScreens: IImages[] = [];

    if (data.viewType === "video" && videoScreensData?.results?.length) {
      processedScreens = videoScreensData.results.map((screen: any) => ({
        ...screen,
        highlighter: false,
        isGrouped: false,
        label: "",
      }));
    } else if (
      data.viewType === "webonboarding" &&
      onboardingScreensData?.results?.length
    ) {
      processedScreens = onboardingScreensData.results.map((screen: any) => ({
        ...screen,
        highlighter: false,
        isGrouped: false,
        label: "",
        app_video: data.idVideo,
      }));
    } else if (data.viewType === "store" && storeScreensData?.results?.length) {
      processedScreens = storeScreensData.results.map((screen: any) => ({
        id: screen.id,
        screen: screen.thumbnail,
        timestamp: "0",
        labels: [],
        highlighter: false,
        isGrouped: false,
        label: "",
        app_video: data.idVideo,
        is_saved: screen.is_saved || false,
        is_saved_pk: screen.is_saved_pk || null,
      }));
    }

    setAppVideoScreens(processedScreens);
  }, [
    videoScreensData,
    onboardingScreensData,
    storeScreensData,
    data.viewType,
    data.idVideo,
  ]);

  // Create InfoItemData array for displaying app metrics
  const InfoItemData = useMemo(() => {
    // Get app data from the appropriate source based on viewType
    let appData: any = {};
    let avsData: any = {};

    if (data.viewType === "video" && data.appData) {
      // For video/replay screens
      appData = data.appData.app || {};
      avsData = data.appData.app?.avs || {};
    } else if (data.viewType === "webonboarding" && data.appData) {
      // For webonboarding screens
      appData = data.appData.app || {};
      avsData = data.appData.app?.avs || {};
    } else if (data.viewType === "store" && data.appData) {
      // For store screens
      appData = data.appData.app || {};
      avsData = data.appData.app?.avs || {};
    }

    return [
      {
        img: "dollarFilterIconActive",
        title: "Monthly Revenue:",
        text:
          appData?.revenue === 0 ||
          (appData?.revenue > 0 && appData?.revenue < 5000)
            ? "<5k"
            : formatNumber(appData?.revenue || 0),
      },
      {
        img: "downloadFilterIconActive",
        title: "Monthly Installs:",
        text:
          appData?.downloads === 0 ||
          (appData?.downloads > 0 && appData?.downloads < 5000)
            ? "<5k"
            : formatNumber(appData?.downloads || 0),
      },
      {
        img: "creditCardLockActive",
        title: "Paywall Type:",
        text: avsData?.paywall_type || "No Paywall",
      },
      {
        img: "dotsGridActive",
        title: "Onboarding Steps:",
        text: avsData?.onboarding_step_count || 0,
      },
    ];
  }, [data.appData, data.viewType]);

  const isLoading = isVideoLoading || isOnboardingLoading || isStoreLoading;

  useEffect(() => {
    dispatch(filterAppsDataActions.setIsScreensLoadingHome(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    const error = videoError || onboardingError || storeError;
    if (error) {
      console.error("Error fetching screens:", error);
    }
  }, [videoError, onboardingError, storeError]);

  const { ref: refIntersection, inView } = useInView({
    threshold: 0,
  });

  const [modalPhoneHorisontalRef] = useAutoAnimate();

  const handleClickPhone = (
    timestamp: number,
    videoId?: number,
    blank?: boolean,
  ) => {
    if (!isScrolling) {
      handleOpenVideoPage(
        timestamp,
        data.slug || "",
        "Saved Library Flow",
        1,
        data.appId || 0,
        videoId || Number(data.idVideo),
        data.viewType === "webonboarding" ? Number(data.idVideo) : undefined,
        me?.username || "",
        organization?.uuid || "",
      );
    }
  };

  const handleImageLoad = () => {
    setLoadedImagesCount((prev) => prev + 1);
  };

  const openProModal = useOpenProModal();

  const handleOpenBillingPage = () => {
    openProModal("Saved Library Flow Modal");
  };

  const handleScroll = () => {
    if (scrollContainerRef.current && !isDragging) {
      setIsScrolling(true);
      setTimeout(() => setIsScrolling(false), 500);
    }
  };

  const handleInView = useCallback((imageId: string, inView: boolean) => {
    setHighlightedImages((prev) => {
      const newImages = new Set(prev);
      if (inView) {
        if (!newImages.has(imageId)) {
          newImages.add(imageId);
          return Array.from(newImages);
        }
      } else {
        if (newImages.has(imageId)) {
          newImages.delete(imageId);
          return Array.from(newImages);
        }
      }
      return prev;
    });
  }, []);

  // Modify handlers to check screen width
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isWideScreen) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current!.offsetLeft);
    setScrollLeft(scrollContainerRef.current!.scrollLeft);
  };

  const handleMouseLeave = () => {
    if (!isWideScreen) return;
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    if (!isWideScreen) return;
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isWideScreen || !isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current!.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current!.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isWideScreen) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current!.offsetLeft);
    setScrollLeft(scrollContainerRef.current!.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isWideScreen || !isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current!.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current!.scrollLeft = scrollLeft - walk;
  };

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    show: boolean;
    imageId: string | null;
    timestamp?: number;
    imageUrl?: string;
  }>({
    x: 0,
    y: 0,
    show: false,
    imageId: null,
  });

  const handleContextMenu = (
    event: React.MouseEvent,
    imageId: string,
    timestamp: number,
    imageUrl: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      show: true,
      imageId,
      timestamp,
      imageUrl,
    });
  };

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu((prev) => ({ ...prev, show: false }));
  }, []);

  // Add function to find and scroll to paywall screens
  const scrollToPaywall = useCallback(() => {
    // Find the first paywall screen in appVideoScreens
    const paywallScreen = appVideoScreens.find(
      (screen) => screen.labels && screen.labels.includes("paywall"),
    );

    if (paywallScreen) {
      // Wait a bit for the DOM to be ready
      setTimeout(() => {
        const targetElement = imageRefs.current[paywallScreen.id];
        if (targetElement && scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const elementRect = targetElement.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          const elementAbsoluteLeft =
            elementRect.left - containerRect.left + container.scrollLeft;
          const containerWidth = container.clientWidth;
          const scrollWidth = container.scrollWidth;
          const maxScrollLeft = scrollWidth - containerWidth;

          // Position the paywall screen in the left portion of the viewport
          let finalScrollLeft = elementAbsoluteLeft; // Changed from 100px to 80px padding from left, so it's a bit more to the right

          // Ensure we don't scroll past the boundaries
          finalScrollLeft = Math.max(
            0,
            Math.min(finalScrollLeft, maxScrollLeft),
          );

          container.scrollTo({
            left: finalScrollLeft,
            behavior: "smooth",
          });

          return true;
        }
      }, 100);
    }
    return false;
  }, [appVideoScreens]);

  // Add paywall click handler
  const handlePaywallTypeClick = useCallback(() => {
    const hasPaywall = InfoItemData.find(
      (item) =>
        item.title === "Paywall Type:" &&
        item.text !== "No Paywall" &&
        item.text !== "None",
    );

    if (hasPaywall) {
      scrollToPaywall();
    }
  }, [InfoItemData, scrollToPaywall]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const contextMenuElement = document.querySelector("[data-context-menu]");
      if (
        contextMenu.show &&
        contextMenuElement &&
        !contextMenuElement.contains(event.target as Node)
      ) {
        handleCloseContextMenu();
      }
    };

    if (contextMenu.show) {
      document.body.style.overflow = "hidden";
      const containerPhotos = document.querySelector(
        `.${styles.containerPhotos}`,
      );
      if (containerPhotos instanceof HTMLElement) {
        containerPhotos.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = "";
      const containerPhotos = document.querySelector(
        `.${styles.containerPhotos}`,
      );
      if (containerPhotos instanceof HTMLElement) {
        containerPhotos.style.overflow = "";
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
      const containerPhotos = document.querySelector(
        `.${styles.containerPhotos}`,
      );
      if (containerPhotos instanceof HTMLElement) {
        containerPhotos.style.overflow = "";
      }
    };
  }, [contextMenu.show, handleCloseContextMenu]);

  // Enhanced scroll logic based on viewType - only run once initially
  useEffect(() => {
    if (data.id && appVideoScreens?.length > 0 && !hasInitialScrolled.current) {
      hasInitialScrolled.current = true;
      setTimeout(() => {
        if (data.viewType === "store") {
          // For store screens: scroll to screen with same id
          let targetScreen = appVideoScreens.find(
            (screen) =>
              screen.id === data.imageStoreId || screen.id === data.id,
          );

          if (
            targetScreen &&
            imageRefs.current[targetScreen.id] &&
            scrollContainerRef.current
          ) {
            const targetElement = imageRefs.current[targetScreen.id]!;
            const container = scrollContainerRef.current;

            const elementRect = targetElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const elementAbsoluteLeft =
              elementRect.left - containerRect.left + container.scrollLeft;
            const containerWidth = container.clientWidth;
            const scrollWidth = container.scrollWidth;
            const maxScrollLeft = scrollWidth - containerWidth;

            let finalScrollLeft = elementAbsoluteLeft;
            if (finalScrollLeft > maxScrollLeft) {
              finalScrollLeft = maxScrollLeft;
            }
            finalScrollLeft = Math.max(0, finalScrollLeft);

            container.scrollTo({
              left: finalScrollLeft,
              behavior: "smooth",
            });
          }
        } else if (data.viewType === "webonboarding") {
          // For webonboarding screens: find screen with same timestamp, or inject if needed
          const targetTimestamp = data.ts;
          let targetScreen = appVideoScreens.find(
            (screen) => parseFloat(screen.timestamp) === targetTimestamp,
          );

          if (targetScreen) {
            // Found exact timestamp match, scroll to it
            if (
              imageRefs.current[targetScreen.id] &&
              scrollContainerRef.current
            ) {
              const targetElement = imageRefs.current[targetScreen.id]!;
              const container = scrollContainerRef.current;

              const elementRect = targetElement.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              const elementAbsoluteLeft =
                elementRect.left - containerRect.left + container.scrollLeft;
              const containerWidth = container.clientWidth;
              const scrollWidth = container.scrollWidth;
              const maxScrollLeft = scrollWidth - containerWidth;

              let finalScrollLeft = elementAbsoluteLeft;
              if (finalScrollLeft > maxScrollLeft) {
                finalScrollLeft = maxScrollLeft;
              }
              finalScrollLeft = Math.max(0, finalScrollLeft);

              container.scrollTo({
                left: finalScrollLeft,
                behavior: "smooth",
              });
            }
          } else {
            // No exact timestamp match, inject the selected screen
            const newScreen: IImages = {
              id: data.id,
              screen: data.image,
              timestamp: data.ts.toString(),
              labels: [],
              highlighter: false,
              isGrouped: false,
              label: "",
              app_video: data.idVideo,
              is_saved: true,
              is_saved_pk: data.id,
            };

            // Find the right position to insert based on timestamp
            const insertIndex = appVideoScreens.findIndex(
              (screen) => parseFloat(screen.timestamp) > targetTimestamp,
            );

            setAppVideoScreens((prev) => {
              const newScreens = [...prev];
              if (insertIndex === -1) {
                // Insert at the end if timestamp is higher than all existing
                newScreens.push(newScreen);
              } else {
                // Insert at the correct position
                newScreens.splice(insertIndex, 0, newScreen);
              }
              return newScreens;
            });

            // After injection, scroll to the new screen
            setTimeout(() => {
              if (imageRefs.current[data.id] && scrollContainerRef.current) {
                const targetElement = imageRefs.current[data.id]!;
                const container = scrollContainerRef.current;

                const elementRect = targetElement.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const elementAbsoluteLeft =
                  elementRect.left - containerRect.left + container.scrollLeft;
                const containerWidth = container.clientWidth;
                const scrollWidth = container.scrollWidth;
                const maxScrollLeft = scrollWidth - containerWidth;

                let finalScrollLeft = elementAbsoluteLeft;
                if (finalScrollLeft > maxScrollLeft) {
                  finalScrollLeft = maxScrollLeft;
                }
                finalScrollLeft = Math.max(0, finalScrollLeft);

                container.scrollTo({
                  left: finalScrollLeft,
                  behavior: "smooth",
                });
              }
            }, 500);
          }
        } else if (data.viewType === "video") {
          // For replay screens: find screen with same timestamp, or inject if needed
          const targetTimestamp = data.ts;
          let targetScreen = appVideoScreens.find(
            (screen) => parseFloat(screen.timestamp) === targetTimestamp,
          );

          if (targetScreen) {
            // Found exact timestamp match, scroll to it
            if (
              imageRefs.current[targetScreen.id] &&
              scrollContainerRef.current
            ) {
              const targetElement = imageRefs.current[targetScreen.id]!;
              const container = scrollContainerRef.current;

              const elementRect = targetElement.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              const elementAbsoluteLeft =
                elementRect.left - containerRect.left + container.scrollLeft;
              const containerWidth = container.clientWidth;
              const scrollWidth = container.scrollWidth;
              const maxScrollLeft = scrollWidth - containerWidth;

              let finalScrollLeft = elementAbsoluteLeft;
              if (finalScrollLeft > maxScrollLeft) {
                finalScrollLeft = maxScrollLeft;
              }
              finalScrollLeft = Math.max(0, finalScrollLeft);

              container.scrollTo({
                left: finalScrollLeft,
                behavior: "smooth",
              });
            }
          } else {
            // No exact timestamp match, inject the selected screen
            const newScreen: IImages = {
              id: data.id,
              screen: data.image,
              timestamp: data.ts.toString(),
              labels: [],
              highlighter: false,
              isGrouped: false,
              label: "",
              app_video: data.idVideo,
              is_saved: true,
              is_saved_pk: data.id,
            };

            // Find the right position to insert based on timestamp
            const insertIndex = appVideoScreens.findIndex(
              (screen) => parseFloat(screen.timestamp) > targetTimestamp,
            );

            setAppVideoScreens((prev) => {
              const newScreens = [...prev];
              if (insertIndex === -1) {
                // Insert at the end if timestamp is higher than all existing
                newScreens.push(newScreen);
              } else {
                // Insert at the correct position
                newScreens.splice(insertIndex, 0, newScreen);
              }
              return newScreens;
            });

            // After injection, scroll to the new screen
            setTimeout(() => {
              if (imageRefs.current[data.id] && scrollContainerRef.current) {
                const targetElement = imageRefs.current[data.id]!;
                const container = scrollContainerRef.current;

                const elementRect = targetElement.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const elementAbsoluteLeft =
                  elementRect.left - containerRect.left + container.scrollLeft;
                const containerWidth = container.clientWidth;
                const scrollWidth = container.scrollWidth;
                const maxScrollLeft = scrollWidth - containerWidth;

                let finalScrollLeft = elementAbsoluteLeft;
                if (finalScrollLeft > maxScrollLeft) {
                  finalScrollLeft = maxScrollLeft;
                }
                finalScrollLeft = Math.max(0, finalScrollLeft);

                container.scrollTo({
                  left: finalScrollLeft,
                  behavior: "smooth",
                });
              }
            }, 500);
          }
        }
      }, 1000);
    }
  }, [
    data.id,
    data.ts,
    data.viewType,
    data.imageStoreId,
    appVideoScreens,
    data.image,
    data.idVideo,
  ]);

  // Helper function to update app video screens state
  const updateAppVideoScreens = (updateFn: (draft: IImages[]) => void) => {
    setAppVideoScreens((prev) => {
      const newScreens = [...prev];
      updateFn(newScreens);
      return newScreens;
    });
  };

  // Update functions for SelectedScreensPopup
  const updateScreensSavedStatus = (
    screenIds: number[],
    isSaved: boolean,
    savedPointIds: Record<number, number> | null,
  ) => {
    // Get the is_saved_pk values BEFORE updating the state (for unsave operations)
    let idsToRemove: number[] = [];
    if (!isSaved && onItemsRemoved) {
      idsToRemove = screenIds
        .map((screenId) => {
          const screen = appVideoScreens.find((s) => s.id === screenId);
          return screen?.is_saved_pk;
        })
        .filter((id): id is number => Boolean(id));
    }

    // Update local state
    setAppVideoScreens((prev) => {
      const updatedScreens = prev.map((screen) => {
        if (screenIds.includes(screen.id)) {
          return {
            ...screen,
            is_saved: isSaved,
            is_saved_pk: savedPointIds ? savedPointIds[screen.id] || 0 : 0,
          };
        }
        return screen;
      });

      // Call parent callbacks with the updated screens data
      if (isSaved && onItemsAdded && savedPointIds) {
        // Get the screens that were saved and transform them to the saved format
        const savedScreens = screenIds
          .map((screenId) => {
            // Use the updated screens instead of the stale appVideoScreens
            const screen = updatedScreens.find((s) => s.id === screenId);
            if (!screen) return null;

            const savedId = savedPointIds[screenId];
            if (!savedId) return null;

            if (data.viewType === "video") {
              // Transform to video screen format
              return {
                id: savedId,
                screen: screen.screen,
                app_video: screen.app_video || data.idVideo,
                app: data.appData?.app || {
                  id: data.appId,
                  name: data.name,
                  slug: data.slug,
                  icon: "",
                },
                ts: screen.timestamp,
                description: "",
                created_at: new Date().toISOString(),
                user: null,
              };
            } else if (data.viewType === "store") {
              // Transform to store screen format - Fix the image/thumbnail mapping
              return {
                id: savedId,
                store_screen: {
                  id: screen.id,
                  image: screen.screen, // Use the thumbnail as both image and thumbnail for now
                  thumbnail: screen.screen,
                  current: true,
                  created_at: new Date().toISOString(),
                  is_saved: true,
                  is_saved_pk: savedId,
                },
                description: "",
                app: data.appData?.app || {
                  id: data.appId,
                  name: data.name,
                  slug: data.slug,
                  icon: "",
                },
                created_at: new Date().toISOString(),
              };
            } else if (data.viewType === "webonboarding") {
              // Transform to onboarding screen format
              return {
                id: savedId,
                screen: screen.screen,
                app_ob_video: data.idVideo,
                app: data.appData?.app || {
                  id: data.appId,
                  name: data.name,
                  slug: data.slug,
                  icon: "",
                },
                ts: screen.timestamp,
                description: "",
                created_at: new Date().toISOString(),
                user: null,
              };
            }
            return null;
          })
          .filter(Boolean);

        if (savedScreens.length > 0) {
          onItemsAdded(savedScreens);
        }
      } else if (!isSaved && onItemsRemoved && idsToRemove.length > 0) {
        // Call onItemsRemoved with the IDs we collected before state update
        onItemsRemoved(idsToRemove);
      }

      return updatedScreens;
    });
  };

  // Determine the SelectedScreensPopup type based on viewType
  const getSelectedScreensType = (): SelectedScreenType => {
    switch (data.viewType) {
      case "video":
        return "appReplays";
      case "store":
        return "appStoreScreens";
      case "webonboarding":
        return "onboardingScreens";
      default:
        return "appReplays";
    }
  };

  // Optimize the prepareDataForDisplay function with proper memoization
  const prepareDataForDisplay = useCallback(() => {
    if (!appVideoScreens?.length) return [];

    return appVideoScreens.reduce(
      (displayData: DisplayDataItem[], image, index) => {
        const labels = image.labels || [];
        const mainLabel = labels.includes("paywall")
          ? "paywall"
          : labels[0] || "";
        const highlighter = false;

        const processedImage = {
          ...image,
          highlighter,
          label: mainLabel,
        };

        if (!Array.isArray(labels) || labels.length === 0) {
          displayData.push({
            label: "",
            images: [{ ...processedImage, label: "" }],
            isGrouped: false,
          });
          return displayData;
        }

        const lastGroup = displayData[displayData.length - 1];
        // Group screens with the same label together (remove timestamp restriction)
        if (lastGroup && lastGroup.label === mainLabel) {
          lastGroup.images.push(processedImage);
        } else {
          displayData.push({
            label: mainLabel,
            images: [processedImage],
            isGrouped: true,
          });
        }

        return displayData;
      },
      [],
    );
  }, [appVideoScreens]);

  // Optimize data state update with proper batching
  useEffect(() => {
    const newData = prepareDataForDisplay();
    if (!newData.length) return;

    setUniqueData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(newData)) {
        return prev;
      }
      return newData;
    });
  }, [prepareDataForDisplay]);

  // Enhanced close function that clears selected screens
  const handleModalClose = useCallback(() => {
    // Clear selected screens based on viewType
    selectedScreensHook.clearSelectedScreens();

    // Call the original onClose
    onClose();
  }, [selectedScreensHook, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleModalClose}
      contentLabel="Saved Library Flow Modal"
      className={styles.modal}
      overlayClassName={styles.overlay}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
    >
      <div
        className={styles.modalContent}
        style={{ height: data.viewType === "video" ? "90vh" : "85vh" }}
      >
        <div className={styles.header}>
          <div
            className={styles.boxLeft}
            onClick={() => {
              handleClickPhone(0, data.idVideo, false);
            }}
          >
            <img
              className={styles.logo}
              src={
                appInfo.icon === "" || !appInfo.icon
                  ? "/img/skeletonLogo.svg"
                  : appInfo.icon
              }
              alt={`${appInfo.name} logo icon`}
              loading="eager"
              draggable={false}
            />
            <div className={styles.title}>
              <h5 className={styles.titleMainText}>{appInfo.name}</h5>
              <h6 className={styles.titleText}>{appInfo.shortname || ""}</h6>
            </div>
          </div>
          <div className={styles.infoDesktopContainer}>
            <div className={styles.infoDesktop}>
              <div className={styles.infoDesktopInner}>
                {(data.viewType === "video" || data.viewType === "store") &&
                  InfoItemData.map((item, index) => {
                    let displayText = item.text;
                    if (
                      (item.title === "Monthly Installs:" ||
                        item.title === "Monthly Revenue:") &&
                      item.text === 0
                    ) {
                      displayText = "<5k";
                    }

                    const hasPaywall =
                      item.title === "Paywall Type:" &&
                      item.text !== "No Paywall" &&
                      item.text !== "None";

                    return (
                      <div key={index}>
                        <InfoPhoneHomePageHorizontal
                          img={item.img}
                          title={item.title}
                          text={displayText}
                          onClick={
                            hasPaywall ? handlePaywallTypeClick : undefined
                          }
                          tooltipText={
                            item.title === "Paywall Type:"
                              ? hasPaywall
                                ? "Bring me there"
                                : "There's no paywall"
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
              </div>
            </div>

            <button className={styles.closeButton} onClick={handleModalClose}>
              <XIcon />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className={styles.loading}>
            <Loader size={40} />
          </div>
        ) : (
          <div className={styles.root}>
            <div ref={phoneHomePageHorizontalRef}>
              <div
                className={styles.containerPhotos}
                ref={scrollContainerRef}
                onScroll={handleScroll}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
              >
                {uniqueData.map((item, index) => (
                  <div
                    key={`group-${index}-${item.label}`}
                    className={styles.photo}
                  >
                    {data.viewType === "video" && (
                      <div className={styles.labelContainer}>
                        {item.isGrouped && (
                          <div
                            className={`${item.label === "paywall" ? styles.spanLabelPaywall : styles.spanLabel}`}
                          >
                            <p className={styles.leftLine} />
                            <span className={styles.stickySpan}>
                              {item.label}
                            </span>
                            <p className={styles.rightLine} />
                          </div>
                        )}
                      </div>
                    )}
                    <div className={styles.photoContainer}>
                      {item.images.map((image, imgIndex) => (
                        <div
                          key={`image-${image.id}-${imgIndex}`}
                          ref={(el) => (imageRefs.current[image.id] = el)}
                        >
                          <div ref={refIntersection}>
                            <MemoizedPhotoItemNew
                              isPaywallOnly={false}
                              setAppVideoScreens={updateAppVideoScreens}
                              idVideo={data.idVideo || 0}
                              image={image}
                              handleClickPhone={handleClickPhone}
                              handleImageLoad={handleImageLoad}
                              isActiveBookmark={true}
                              onInView={handleInView}
                              apiDataVideosScreens={appVideoScreens}
                              handleOpenBillingPage={handleOpenBillingPage}
                              onContextMenu={handleContextMenu}
                              containerRef={scrollContainerRef}
                              refetchData={() => {
                                if (data.viewType === "video") {
                                  refetchVideoScreens();
                                } else if (data.viewType === "webonboarding") {
                                  refetchOnboardingScreens();
                                } else if (data.viewType === "store") {
                                  refetchStoreScreens();
                                }
                              }}
                              ScreenNotBlurredID={data.id}
                              appInfo={{
                                appName: appInfo.name,
                                appIcon: appInfo.icon,
                                appDescription: appInfo.shortname,
                              }}
                              isSavedLibrary
                              screenType={data.viewType}
                              selectedScreensHook={selectedScreensHook}
                              viewType={data.viewType}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div
              className={styles.modalWindow}
              ref={modalPhoneHorisontalRef}
            ></div>
            {contextMenu.show && (
              <RightClickModal
                onlyCopy
                x={contextMenu.x}
                y={contextMenu.y}
                onClose={handleCloseContextMenu}
                imageUrl={contextMenu.imageUrl || ""}
                imageId={contextMenu.imageId || ""}
                handleOpenInNewTab={(timestamp: number) =>
                  handleClickPhone(timestamp)
                }
                timestamp={contextMenu.timestamp || 0}
                data-context-menu
                avsID={data.idVideo}
                refetchData={() => {
                  if (data.viewType === "video") {
                    refetchVideoScreens();
                  } else if (data.viewType === "webonboarding") {
                    refetchOnboardingScreens();
                  } else if (data.viewType === "store") {
                    refetchStoreScreens();
                  }
                }}
                imageType={
                  data.viewType === "video"
                    ? "video"
                    : data.viewType === "webonboarding"
                      ? "webOnboarding"
                      : "store"
                }
                containerRef={scrollContainerRef}
              />
            )}
          </div>
        )}

        {/* Add SelectedScreensPopup when screens are selected */}
        {selectedScreensCount > 0 && (
          <SelectedScreensPopup
            type={getSelectedScreensType()}
            updateScreensSavedStatus={updateScreensSavedStatus}
            updateReplayScreensSavedStatus={updateScreensSavedStatus}
            setAppStoreScreens={setAppVideoScreens}
          />
        )}
      </div>
    </Modal>
  );
};

export default SavedLibraryFlowModal;
